import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Snackbar,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  CheckCircle as ApproveIcon,
  Flag as FlagIcon,
  Comment as CommentIcon,
  Article as PostIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from './utils/api';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`moderation-tabpanel-${index}`}
      aria-labelledby={`moderation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function CommunityModerationManager() {
  const [tabValue, setTabValue] = useState(0);
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [moderationActions, setModerationActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [actionDialog, setActionDialog] = useState(false);
  const [warningDialog, setWarningDialog] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [warningLoading, setWarningLoading] = useState(false);
  const [warningData, setWarningData] = useState({
    reason: '',
    level: 'LOW',
    expiresAt: null,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filter, setFilter] = useState({
    contentType: 'all',
    moderationStatus: 'all',
    spamOnly: false,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Debug: Check if Keycloak and token are available
      console.log('🔐 Debug - Keycloak check:', {
        keycloakExists: !!window.keycloak,
        token: window.keycloak?.token ? 'Present' : 'Missing',
        authenticated: window.keycloak?.authenticated,
        tokenParsed: window.keycloak?.tokenParsed?.preferred_username,
        roles: {
          realm: window.keycloak?.tokenParsed?.realm_access?.roles || [],
          resources: Object.keys(window.keycloak?.tokenParsed?.resource_access || {})
        }
      });

      const [postsRes, commentsRes, usersRes, actionsRes] = await Promise.all([
        api.get('/admin/community/posts'),
        api.get('/admin/community/comments'),
        api.get('/admin/community/users'),
        api.get('/admin/community/moderation-actions'),
      ]);

      setPosts(postsRes.data.data || []);
      setComments(commentsRes.data.data || []);
      setUsers(usersRes.data.data || []);
      setModerationActions(actionsRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch moderation data:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load moderation data',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewDetail = (item, type) => {
    setSelectedItem({ ...item, type });
    setDetailDialog(true);
  };

  const handleAction = async (item, action) => {
    // Ensure the item has the correct type set
    let itemWithType = { ...item };
    
    // If type is not set, determine it based on the action or item structure
    if (!itemWithType.type) {
      if (action === 'delete_post' || (item.hashtags !== undefined && item.country !== undefined)) {
        itemWithType.type = 'post';
      } else if (action === 'delete_comment' || item.postId) {
        itemWithType.type = 'comment';
      }
    }
    
    console.log('🔧 Debug handleAction:', {
      action,
      itemId: item.id,
      originalType: item.type,
      determinedType: itemWithType.type,
      hasPostId: !!item.postId,
      hasHashtags: !!item.hashtags,
      hasCountry: !!item.country
    });
    
    setSelectedItem(itemWithType);
    setActionType(action);
    
    if (action === 'warn') {
      setWarningDialog(true);
    } else {
      setActionDialog(true);
    }
  };

  const confirmAction = async () => {
    setActionLoading(true);
    try {
      let endpoint = '';
      let method = 'POST';
      let data = {};

      // Debug logging to help identify the issue
      console.log('🔧 Debug confirmAction:', {
        actionType,
        selectedItem: {
          id: selectedItem?.id,
          type: selectedItem?.type,
          content: selectedItem?.content?.substring(0, 50),
          moderationStatus: selectedItem?.moderationStatus
        }
      });

      // Determine content type based on the item structure
      let contentType = '';
      if (selectedItem?.type) {
        contentType = selectedItem.type.toUpperCase();
      } else if (selectedItem?.postId) {
        // If it has a postId, it's a comment
        contentType = 'COMMENT';
      } else if (selectedItem?.content && !selectedItem?.postId) {
        // If it has content but no postId, it's likely a post
        contentType = 'POST';
      }

      console.log('🔧 Debug contentType determined:', contentType);

      switch (actionType) {
        case 'delete_post':
          endpoint = `/admin/community/posts/${selectedItem.id}`;
          method = 'DELETE';
          break;
        case 'delete_comment':
          endpoint = `/admin/community/comments/${selectedItem.id}`;
          method = 'DELETE';
          break;
        case 'approve':
          endpoint = `/admin/community/moderate/${selectedItem.id}`;
          data = { action: 'APPROVED', contentType };
          break;
        case 'flag':
          endpoint = `/admin/community/moderate/${selectedItem.id}`;
          data = { action: 'FLAGGED', contentType };
          break;
        case 'block_user':
          endpoint = `/admin/community/users/${selectedItem.userId || selectedItem.id}/block`;
          break;
        default:
          return;
      }

      console.log('🔧 Debug API call:', { method, endpoint, data });

      if (method === 'DELETE') {
        await api.delete(endpoint);
      } else {
        await api.post(endpoint, data);
      }

      setSnackbar({
        open: true,
        message: `Action completed successfully`,
        severity: 'success',
      });

      fetchData(); // Refresh data
    } catch (error) {
      console.error('Action failed:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Action failed. Please try again.',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
      setActionDialog(false);
      setSelectedItem(null);
      setActionType('');
    }
  };

  const handleWarning = async () => {
    setWarningLoading(true);
    try {
      await api.post(`/admin/community/users/${selectedItem.userId}/warn`, warningData);
      
      setSnackbar({
        open: true,
        message: 'Warning issued successfully',
        severity: 'success',
      });

      fetchData(); // Refresh data
    } catch (error) {
      console.error('Warning failed:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to issue warning. Please try again.',
        severity: 'error',
      });
    } finally {
      setWarningLoading(false);
      setWarningDialog(false);
      setSelectedItem(null);
      setWarningData({ reason: '', level: 'LOW', expiresAt: null });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'FLAGGED': return 'error';
      case 'PENDING': return 'warning';
      case 'REMOVED': return 'default';
      default: return 'default';
    }
  };

  const getSpamScoreColor = (score) => {
    if (score >= 0.8) return 'error';
    if (score >= 0.5) return 'warning';
    return 'success';
  };

  const renderPostsTable = () => {
    const filteredPosts = posts.filter(post => {
      if (filter.moderationStatus !== 'all' && post.moderationStatus !== filter.moderationStatus) return false;
      if (filter.spamOnly && !post.isSpam) return false;
      return true;
    });

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Content</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Hashtags</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Spam Score</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPosts.map((post) => (
              <TableRow key={post.id}>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                    {post.content}
                  </Typography>
                  {post.isSpam && (
                    <Chip label="SPAM" color="error" size="small" sx={{ mt: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {post.user?.firstName} {post.user?.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {post.user?.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {post.country ? (
                    <Chip
                      label={post.country.name}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No country
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                    {post.hashtags && post.hashtags.length > 0 ? (
                      post.hashtags.map((hashtagUse) => (
                        <Chip
                          key={hashtagUse.id}
                          label={`#${hashtagUse.hashtag.name}`}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: '20px',
                            backgroundColor: '#1976D220',
                            color: '#1976D2',
                            border: '1px solid #1976D240',
                            fontWeight: '500',
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No hashtags
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={post.moderationStatus}
                    color={getStatusColor(post.moderationStatus)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {post.spamScore && (
                    <Chip
                      label={`${(post.spamScore * 100).toFixed(1)}%`}
                      color={getSpamScoreColor(post.spamScore)}
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {new Date(post.createdAt).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton onClick={() => handleViewDetail(post, 'post')}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Post">
                    <IconButton 
                      color="error" 
                      onClick={() => handleAction(post, 'delete_post')}
                      disabled={actionLoading || warningLoading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                  {post.moderationStatus !== 'REMOVED' && (
                    <>
                      <Tooltip title="Approve">
                        <IconButton 
                          color="success" 
                          onClick={() => handleAction(post, 'approve')}
                          disabled={post.moderationStatus === 'APPROVED' || actionLoading || warningLoading}
                        >
                          <ApproveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Flag">
                        <IconButton 
                          color="warning" 
                          onClick={() => handleAction(post, 'flag')}
                          disabled={post.moderationStatus === 'FLAGGED' || actionLoading || warningLoading}
                        >
                          <FlagIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Warn User">
                    <IconButton 
                      color="warning" 
                      onClick={() => handleAction(post, 'warn')}
                      disabled={actionLoading || warningLoading}
                    >
                      <WarningIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderCommentsTable = () => {
    const filteredComments = comments.filter(comment => {
      if (filter.moderationStatus !== 'all' && comment.moderationStatus !== filter.moderationStatus) return false;
      if (filter.spamOnly && !comment.isSpam) return false;
      return true;
    });

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Content</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Post</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Spam Score</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredComments.map((comment) => (
              <TableRow key={comment.id}>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {comment.content}
                  </Typography>
                  {comment.isSpam && (
                    <Chip label="SPAM" color="error" size="small" sx={{ mt: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {comment.user?.firstName} {comment.user?.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {comment.user?.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>
                    {comment.post?.content?.substring(0, 50)}...
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={comment.moderationStatus}
                    color={getStatusColor(comment.moderationStatus)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {comment.spamScore && (
                    <Chip
                      label={`${(comment.spamScore * 100).toFixed(1)}%`}
                      color={getSpamScoreColor(comment.spamScore)}
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {new Date(comment.createdAt).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton onClick={() => handleViewDetail(comment, 'comment')}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Comment">
                    <IconButton 
                      color="error" 
                      onClick={() => handleAction(comment, 'delete_comment')}
                      disabled={actionLoading || warningLoading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                  {comment.moderationStatus !== 'REMOVED' && (
                    <>
                      <Tooltip title="Approve">
                        <IconButton 
                          color="success" 
                          onClick={() => handleAction(comment, 'approve')}
                          disabled={comment.moderationStatus === 'APPROVED' || actionLoading || warningLoading}
                        >
                          <ApproveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Flag">
                        <IconButton 
                          color="warning" 
                          onClick={() => handleAction(comment, 'flag')}
                          disabled={comment.moderationStatus === 'FLAGGED' || actionLoading || warningLoading}
                        >
                          <FlagIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Warn User">
                    <IconButton 
                      color="warning" 
                      onClick={() => handleAction(comment, 'warn')}
                      disabled={actionLoading || warningLoading}
                    >
                      <WarningIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderUsersTable = () => {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Warning Count</TableCell>
              <TableCell>Ghost Mode</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Typography variant="body2">
                    {user.firstName} {user.lastName}
                  </Typography>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Badge badgeContent={user.warningCount} color="error">
                    <WarningIcon />
                  </Badge>
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.isGhostMode ? 'Yes' : 'No'}
                    color={user.isGhostMode ? 'default' : 'primary'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton onClick={() => handleViewDetail(user, 'user')}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Issue Warning">
                    <IconButton 
                      color="warning" 
                      onClick={() => handleAction(user, 'warn')}
                      disabled={actionLoading || warningLoading}
                    >
                      <WarningIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Block User">
                    <IconButton 
                      color="error" 
                      onClick={() => handleAction(user, 'block_user')}
                      disabled={actionLoading || warningLoading}
                    >
                      <BlockIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderFilters = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filter.moderationStatus}
              onChange={(e) => setFilter({ ...filter, moderationStatus: e.target.value })}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="FLAGGED">Flagged</MenuItem>
              <MenuItem value="REMOVED">Removed</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              select
              fullWidth
              label="Content Type"
              value={filter.contentType}
              onChange={(e) => setFilter({ ...filter, contentType: e.target.value })}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="posts">Posts Only</MenuItem>
              <MenuItem value="comments">Comments Only</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Button
              variant={filter.spamOnly ? 'contained' : 'outlined'}
              color="error"
              onClick={() => setFilter({ ...filter, spamOnly: !filter.spamOnly })}
            >
              {filter.spamOnly ? 'Show All' : 'Spam Only'}
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Community Moderation
        </Typography>

        {renderFilters()}

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab 
            icon={<PostIcon />} 
            label={`Posts (${posts.length})`} 
            iconPosition="start"
          />
          <Tab 
            icon={<CommentIcon />} 
            label={`Comments (${comments.length})`} 
            iconPosition="start"
          />
          <Tab 
            icon={<PersonIcon />} 
            label={`Users (${users.length})`} 
            iconPosition="start"
          />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {renderPostsTable()}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {renderCommentsTable()}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {renderUsersTable()}
        </TabPanel>

        {/* Detail Dialog */}
        <Dialog 
          open={detailDialog} 
          onClose={() => setDetailDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedItem?.type === 'post' ? 'Post Details' : 
             selectedItem?.type === 'comment' ? 'Comment Details' : 'User Details'}
          </DialogTitle>
          <DialogContent>
            {selectedItem && (
              <Box>
                {selectedItem.type === 'user' ? (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2">Name:</Typography>
                      <Typography>{selectedItem.firstName} {selectedItem.lastName}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2">Email:</Typography>
                      <Typography>{selectedItem.email}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2">Warning Count:</Typography>
                      <Typography>{selectedItem.warningCount}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2">Ghost Mode:</Typography>
                      <Typography>{selectedItem.isGhostMode ? 'Enabled' : 'Disabled'}</Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <Typography variant="subtitle2">Content:</Typography>
                      <Typography>{selectedItem.content}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2">Author:</Typography>
                      <Typography>
                        {selectedItem.user?.firstName} {selectedItem.user?.lastName}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2">Status:</Typography>
                      <Chip
                        label={selectedItem.moderationStatus}
                        color={getStatusColor(selectedItem.moderationStatus)}
                        size="small"
                      />
                    </Grid>
                    {selectedItem.spamScore && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2">Spam Score:</Typography>
                        <Chip
                          label={`${(selectedItem.spamScore * 100).toFixed(1)}%`}
                          color={getSpamScoreColor(selectedItem.spamScore)}
                          size="small"
                        />
                      </Grid>
                    )}
                    {selectedItem.moderationReason && (
                      <Grid size={12}>
                        <Typography variant="subtitle2">Moderation Reason:</Typography>
                        <Typography>{selectedItem.moderationReason}</Typography>
                      </Grid>
                    )}
                    {selectedItem.country && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2">Country:</Typography>
                        <Chip
                          label={selectedItem.country.name}
                          size="small"
                          variant="outlined"
                        />
                      </Grid>
                    )}
                    {selectedItem.hashtags && selectedItem.hashtags.length > 0 && (
                      <Grid size={12}>
                        <Typography variant="subtitle2">Hashtags:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {selectedItem.hashtags.map((hashtagUse) => (
                            <Chip
                              key={hashtagUse.id}
                              label={`#${hashtagUse.hashtag.name}`}
                              size="small"
                              sx={{
                                backgroundColor: '#1976D220',
                                color: '#1976D2',
                                border: '1px solid #1976D240',
                                fontWeight: '500',
                              }}
                            />
                          ))}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                            Usage counts: {selectedItem.hashtags.map(h => `${h.hashtag.name}(${h.hashtag.count})`).join(', ')}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Action Confirmation Dialog */}
        <Dialog 
          open={actionDialog} 
          onClose={() => !actionLoading && setActionDialog(false)}
          disableEscapeKeyDown={actionLoading}
        >
          <DialogTitle>Confirm Action</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to {actionType.replace('_', ' ')} this item?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setActionDialog(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmAction} 
              color="error" 
              variant="contained"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : null}
            >
              {actionLoading ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Warning Dialog */}
        <Dialog 
          open={warningDialog} 
          onClose={() => !warningLoading && setWarningDialog(false)}
          disableEscapeKeyDown={warningLoading}
        >
          <DialogTitle>Issue Warning</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Warning Reason"
                  multiline
                  rows={3}
                  value={warningData.reason}
                  onChange={(e) => setWarningData({ ...warningData, reason: e.target.value })}
                  required
                  disabled={warningLoading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Warning Level"
                  value={warningData.level}
                  onChange={(e) => setWarningData({ ...warningData, level: e.target.value })}
                  disabled={warningLoading}
                >
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                  <MenuItem value="CRITICAL">Critical</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DateTimePicker
                  label="Expires At (Optional)"
                  value={warningData.expiresAt}
                  onChange={(newValue) => setWarningData({ ...warningData, expiresAt: newValue })}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  disabled={warningLoading}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setWarningDialog(false)}
              disabled={warningLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleWarning} 
              color="warning" 
              variant="contained"
              disabled={!warningData.reason.trim() || warningLoading}
              startIcon={warningLoading ? <CircularProgress size={16} /> : null}
            >
              {warningLoading ? 'Issuing Warning...' : 'Issue Warning'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}

export default CommunityModerationManager;